import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { ConfigOptions, ConfigService } from '@wilson/config';
import {
  Activity,
  ActivityCategory,
  BulkUpdateActivitiesDto,
  ResolvedActivity,
} from '@wilson/interfaces';
import { stringify } from 'qs';
import { Observable, firstValueFrom, map, shareReplay } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class ActivitiesGateway {
  private readonly httpClient = inject(HttpClient);
  private readonly config = inject<ConfigOptions>(ConfigService);

  /**
   * @deprecated
   */
  private enabledCategories$ = this.getAllActivityCategories().pipe(
    map((categories) => {
      return categories.filter((category) => category.isEnabled);
    }),
    shareReplay(1),
  );

  public getActivityCategory(id: string): Observable<ActivityCategory> {
    return this.httpClient.get<ActivityCategory>(
      `${this.config.host}/activity-categories/${id}`,
    );
  }

  /**
   * @deprecated
   */
  public getEnabledActivityCategories(): Observable<ActivityCategory[]> {
    return this.enabledCategories$;
  }

  getAllActivityCategories(): Observable<ActivityCategory[]> {
    const params = stringify({
      relations: ['sectors', 'activityCategoryDefaultSetting'],
    });
    return this.httpClient.get<ActivityCategory[]>(
      `${this.config.host}/activity-categories?${params}`,
    );
  }

  /**
   * Saves the company's enabled state and protective working time value for
   * each given activity category.
   *
   * An omitted `isProtectiveWorkingTime` keeps the stored value of an existing
   * row and stores null for a new row.
   */
  updateActivityCategories(
    activities: {
      activityCategoryId: string;
      isEnabled: boolean;
      isProtectiveWorkingTime?: boolean;
    }[],
  ) {
    return this.httpClient.put(
      `${this.config.host}/activity-categories/organizational-unit`,
      {
        items: activities,
      },
    );
  }

  public getActivity(id: string): Observable<Activity> {
    return this.httpClient.get<Activity>(
      `${this.config.host}/activities/${id}`,
    );
  }

  public getResolvedActivity(id: string): Observable<ResolvedActivity[]> {
    return this.httpClient.post<ResolvedActivity[]>(
      `${this.config.host}/activities/resolved`,
      { id },
    );
  }

  public createActivities(activities: Activity[]): Observable<Activity[]> {
    return this.httpClient.post<Activity[]>(`${this.config.host}/activities`, {
      items: activities,
    });
  }

  public bulkUpdateActivities(
    activitiesToUpdate: BulkUpdateActivitiesDto[],
  ): Observable<{ affected: number }> {
    return this.httpClient.patch<{ affected: number }>(
      `${this.config.host}/activities/bulk/update`,
      {
        items: activitiesToUpdate,
      },
    );
  }

  public bulkDeleteActivities(activityIds: string[]): Observable<Activity[]> {
    return this.httpClient.delete<Activity[]>(
      `${this.config.host}/activities/bulk/remove`,
      {
        body: {
          ids: activityIds,
        },
      },
    );
  }

  public getActivitiesByShiftId(shiftId: string): Observable<Activity[]> {
    return this.httpClient.post<Activity[]>(
      `${this.config.host}/activities/search`,
      { shiftId },
    );
  }

  public updateActivity(activity: Partial<Activity>): Promise<Activity> {
    return firstValueFrom(
      this.httpClient.patch<Activity>(
        `${this.config.host}/activities/${activity.id}`,
        activity,
      ),
    );
  }

  public deleteActivity(activityId: string): Promise<Activity> {
    return firstValueFrom(
      this.httpClient.delete<Activity>(
        `${this.config.host}/activities/${activityId}`,
      ),
    );
  }

  public deleteActivityReports(activityId: string): Promise<Activity> {
    return firstValueFrom(
      this.httpClient.delete<Activity>(
        `${this.config.host}/activities/${activityId}/reports`,
      ),
    );
  }
}
